// CardAccount.cshtml: the card and its balance, or "being set up" with the way back to onboarding.
import { cardNumberLine, firstName, money, shortDate, useFeatures } from '@qor/web-shared';
import { Link, Navigate } from 'react-router-dom';
import { cardStatusBadge, lastReloadAt, useCardAccount } from '../account';
import { usePageTitle } from '../components/CustomerLayout';
import { useUser } from '../components/RequireAuth';
import { onboardingPath } from '../routing';

export function CardAccountPage() {
  const user = useUser();
  usePageTitle('Your Account');
  if (!user.providerUserReference) return <Navigate to={onboardingPath(user.provider)} replace />;
  return <CardAccountView reference={user.providerUserReference} />;
}

function CardAccountView({ reference }: { reference: string }) {
  const user = useUser();
  const features = useFeatures(user.email);
  const { account, loaded, transactions, refresh, refreshing } = useCardAccount(reference, true);
  const connected = account?.isConnected === true;
  const currency = account?.currency ?? 'CAD';
  if (!features.loaded) return null;
  const badge = cardStatusBadge(account?.cardStatus, user.provider);

  return (
    <>
      <div className="qcw-greeting-row">
        <div>
          <h1 className="qcw-greeting">Welcome back, {firstName(user.displayName)}</h1>
          <p className="qcw-subgreeting">Here&apos;s what&apos;s happening with your card.</p>
        </div>
        <span className="qcw-last-reload-chip">Last reload: {shortDate(lastReloadAt(transactions)) ?? 'Never'}</span>
      </div>

      {loaded && connected && account && badge.hint && (
        <p className="qcw-card-status-hint" role="note">
          {badge.hint}
        </p>
      )}
      {!loaded ? null : connected && account ? (
        <section className="qcw-hero" aria-label="Card and balance">
          <div className="qcw-card-visual">
            <div className="qcw-card-top">
              <span className={`qcw-card-status qcw-card-status-${badge.tone}`}>{badge.label}</span>
            </div>

            <div className="qcw-card-chip-row">
              <svg className="qcw-card-chip" viewBox="0 0 48 36" aria-hidden="true">
                <rect x="1" y="1" width="46" height="34" rx="6" fill="#d8dde3" stroke="#9aa3ad" strokeWidth="1.5" />
                <line x1="17" y1="1" x2="17" y2="35" stroke="#9aa3ad" strokeWidth="1.2" />
                <line x1="31" y1="1" x2="31" y2="35" stroke="#9aa3ad" strokeWidth="1.2" />
                <line x1="1" y1="12" x2="47" y2="12" stroke="#9aa3ad" strokeWidth="1.2" />
                <line x1="1" y1="24" x2="47" y2="24" stroke="#9aa3ad" strokeWidth="1.2" />
                <rect x="17" y="12" width="14" height="12" fill="#c2c9d1" />
              </svg>
              <svg className="qcw-card-contactless" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M6 8a8.5 8.5 0 0 1 0 8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
                <path
                  d="M10 4a13.5 13.5 0 0 1 0 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
                <path
                  d="M14 0.5a18.5 18.5 0 0 1 0 23"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <div>
              <div className="qcw-card-number" data-testid="card-number">
                {cardNumberLine(account, user.provider).text}
              </div>
              <div className="qcw-card-name">
                {account.displayName?.trim() ? account.displayName : user.displayName}
              </div>
            </div>
          </div>

          <div className="qcw-balance-panel">
            {features.isEnabled('CheckBalance') && (
              <div>
                <p className="qcw-balance-label">Available balance</p>
                <p className="qcw-balance-value">
                  {money(account.availableBalance, currency)} <small>{currency}</small>
                </p>
              </div>
            )}
            <button type="button" className="qcw-refresh" onClick={() => void refresh()} disabled={refreshing}>
              Refresh balance
            </button>
          </div>
        </section>
      ) : (
        <section className="qcw-section" aria-label="Card setup">
          <h2 className="qcw-section-title">Your card is being set up</h2>
          <p className="qcw-empty">
            We don&apos;t have a card on file for your account yet. If you recently applied, this can take a few
            minutes.
          </p>
          <div className="qcw-section-body">
            <Link className="qcw-btn-primary" to={onboardingPath(user.provider)}>
              Continue setup
            </Link>
          </div>
        </section>
      )}
    </>
  );
}
